import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Clapperboard, FileText, Film, ImageDown, Play, Route, Table2 } from 'lucide-react';
import { useHelp } from '../../contexts/HelpContext';
import { WHATS_NEW } from '../../data/whatsNew';
import BetaBadge from '../common/BetaBadge';
import styles from './WhatsNew.module.css';

// Named rather than looked up off the whole lucide namespace: importing the
// module by name lets the bundler drop the ~1400 icons nobody asked for.
const ICONS = { Box, Clapperboard, FileText, Film, ImageDown, Route, Table2 };

const COLLAPSED_KEY = 'trackapp:whatsNewCollapsed';
const SEEN_KEY      = 'trackapp:whatsNewSeen';

// Long enough that expanding and immediately collapsing again — or landing on
// the page mid-scroll — doesn't record entries the driver never actually saw.
const MARK_SEEN_DELAY_MS = 1500;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDate(ym) {
  const [y, m] = String(ym || '').split('-');
  const name = MONTHS[Number(m) - 1];
  return name && y ? `${name} ${y}` : '';
}

function readSeen() {
  try {
    const raw = JSON.parse(localStorage.getItem(SEEN_KEY) || '[]');
    return new Set(Array.isArray(raw) ? raw : []);
  } catch {
    return new Set();
  }
}

export default function WhatsNew() {
  const { available, articles, ensureArticlesLoaded, openHelp } = useHelp();

  const entries = useMemo(
    () => [...WHATS_NEW].sort((a, b) => String(b.date).localeCompare(String(a.date))),
    [],
  );

  // Closed by default: the rail is an aside, and the job this page exists for is
  // uploading a file or opening a session. The unseen count in the header is
  // what does the advertising — and because nothing is recorded as seen while
  // the rail is shut, that count survives until someone actually opens it.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSED_KEY) !== 'false'; } catch { return true; }
  });
  const [playing, setPlaying] = useState(null);   // id of the entry whose video is mounted

  // Which entries count as new is decided ONCE, at mount, and never re-read.
  // Recording an entry as seen must not make its badge disappear under the eyes
  // of the person currently reading it; the rail simply looks the same for the
  // whole visit and is quiet on the next one.
  const unseenAtMount = useRef(null);
  if (unseenAtMount.current === null) {
    const seen = readSeen();
    unseenAtMount.current = new Set(entries.filter(e => !seen.has(e.id)).map(e => e.id));
  }
  const unseenCount = unseenAtMount.current.size;

  // The Help articles decide which cards get a "Learn more" link, so they have
  // to be fetched even though nothing here has opened the Help panel yet.
  useEffect(() => { if (available) ensureArticlesLoaded(); }, [available, ensureArticlesLoaded]);

  // Record everything on screen as seen once the rail has been open for a beat.
  // This is a write only — no state changes, so nothing on screen moves.
  useEffect(() => {
    if (collapsed || unseenCount === 0) return undefined;
    const timer = setTimeout(() => {
      try {
        const next = readSeen();
        entries.forEach(e => next.add(e.id));
        localStorage.setItem(SEEN_KEY, JSON.stringify([...next]));
      } catch { /* private mode — the rail just stays new */ }
    }, MARK_SEEN_DELAY_MS);
    return () => clearTimeout(timer);
  }, [collapsed, unseenCount, entries]);

  const toggle = useCallback(() => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(COLLAPSED_KEY, String(next)); } catch { /* private mode */ }
      return next;
    });
  }, []);

  const articleFor = useCallback((help) => {
    if (!available || !help) return null;
    return articles.find(a => a.page === help.page && a.panel === help.panel) || null;
  }, [available, articles]);

  if (entries.length === 0) return null;

  return (
    <section className={styles.wrap} aria-labelledby="whats-new-title">
      <div
        className={styles.header}
        onClick={toggle}
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
      >
        <span className={styles.toggle} aria-hidden="true">{collapsed ? '▶' : '▼'}</span>
        <h3 className={styles.title} id="whats-new-title">What&rsquo;s New</h3>
        {unseenCount > 0 && (
          <span className={styles.countBadge}>{unseenCount} new</span>
        )}
      </div>

      {!collapsed && (
        <div className={styles.grid}>
          {entries.map((entry, i) => {
            const Icon = ICONS[entry.icon] || Box;
            const article = articleFor(entry.help);
            const featured = i === 0 && !!entry.poster;
            const isNew = unseenAtMount.current.has(entry.id);
            return (
              <article
                key={entry.id}
                className={featured ? `${styles.card} ${styles.cardFeatured}` : styles.card}
              >
                {featured && (
                  <div className={styles.media}>
                    {playing === entry.id && entry.video ? (
                      // Mounted only on click, so a page load never pulls the clip.
                      <video
                        className={styles.video}
                        poster={entry.poster}
                        preload="none"
                        autoPlay
                        muted
                        loop
                        playsInline
                        controls
                      >
                        {entry.video.map(src => (
                          <source
                            key={src}
                            src={src}
                            type={src.endsWith('.webm') ? 'video/webm' : 'video/mp4'}
                          />
                        ))}
                      </video>
                    ) : (
                      <button
                        type="button"
                        className={styles.posterBtn}
                        onClick={() => setPlaying(entry.id)}
                        aria-label={`Play a clip of ${entry.title}`}
                      >
                        <img className={styles.poster} src={entry.poster} alt="" loading="lazy" />
                        <span className={styles.playBadge} aria-hidden="true">
                          <Play size={18} fill="currentColor" />
                        </span>
                      </button>
                    )}
                  </div>
                )}

                <div className={styles.cardHead}>
                  <span className={styles.icon} aria-hidden="true"><Icon size={16} /></span>
                  <h4 className={styles.cardTitle}>{entry.title}{entry.beta && <BetaBadge />}</h4>
                </div>

                {/* Date and the NEW pill share a line: hung off the title they
                    wrap onto one of their own the moment a title takes two. */}
                <div className={styles.meta}>
                  <span className={styles.date}>{formatDate(entry.date)}</span>
                  {isNew && <span className={styles.newBadge}>New</span>}
                </div>

                <p className={styles.blurb}>{entry.blurb}</p>

                {article && (
                  <button
                    type="button"
                    className={styles.learnMore}
                    onClick={() => openHelp(entry.help)}
                  >
                    Learn more &rarr;
                  </button>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
